import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Users, Plus, Loader2, ShieldCheck, UserCircle, Pencil, CheckCircle2, XCircle,
} from "lucide-react";

const EMPTY = { full_name: "", email: "", password: "", role: "operatore", office_id: "", phone: "" };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, o] = await Promise.all([api.get("/users"), api.get("/offices")]);
      setUsers(u.data);
      setOffices(o.data);
    } catch (e) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const officeName = (id) => offices.find((o) => o.id === id)?.name || "—";

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setError("");
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ full_name: u.full_name, email: u.email, password: "", role: u.role, office_id: u.office_id || "", phone: u.phone || "" });
    setError("");
    setOpen(true);
  };

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      if (editing) {
        const payload = { full_name: form.full_name, role: form.role, office_id: form.office_id || null, phone: form.phone };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Utente aggiornato");
      } else {
        await api.post("/users", {
          full_name: form.full_name, email: form.email, password: form.password,
          role: form.role, office_id: form.office_id || null, phone: form.phone,
        });
        toast.success("Utente creato");
      }
      setOpen(false);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Utente disattivato" : "Utente attivato");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Gestione Utenti</h1>
          <p className="mt-1 text-sm text-slate-500">Crea e gestisci gli operatori. Solo la Direzione può modificare.</p>
        </div>
        <Button onClick={openCreate} data-testid="nuovo-utente-button" className="gap-2">
          <Plus className="h-4 w-4" /> Nuovo Utente
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : (
          <div className="ds-table-scroll">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-left">
                <tr className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Nome</th>
                  <th className="hidden px-4 py-3 md:table-cell">Email</th>
                  <th className="px-4 py-3">Ruolo</th>
                  <th className="hidden px-4 py-3 sm:table-cell">Sede</th>
                  <th className="px-4 py-3">Stato</th>
                  <th className="px-4 py-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50/80" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                          <UserCircle className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="truncate font-medium text-slate-900">{u.full_name}</div>
                          <div className="truncate font-mono text-[11px] text-slate-500 md:hidden">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="hidden px-4 py-3 font-mono text-xs text-slate-600 md:table-cell">{u.email}</td>
                    <td className="px-4 py-3">
                      {u.role === "admin" ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-brand/25 bg-brand/10 px-2.5 py-1 text-xs font-semibold text-brand">
                          <ShieldCheck className="h-3.5 w-3.5" /> Direzione
                        </span>
                      ) : u.role === "backoffice" ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800">
                          <ShieldCheck className="h-3.5 w-3.5" /> Back Office
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700">
                          <UserCircle className="h-3.5 w-3.5" /> Operatore
                        </span>
                      )}
                    </td>
                    <td className="hidden px-4 py-3 text-slate-600 sm:table-cell">{officeName(u.office_id)}</td>
                    <td className="px-4 py-3">
                      {u.active ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Attivo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-800">
                          <XCircle className="h-3.5 w-3.5" /> Disattivo
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} className="gap-1.5">
                          <Pencil className="h-3.5 w-3.5" /> Modifica
                        </Button>
                        <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} data-testid={`toggle-user-${u.id}`} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Modifica utente" : "Nuovo utente"}</DialogTitle>
            <DialogDescription>{editing ? "Aggiorna i dati dell'operatore." : "Compila i campi per creare un nuovo operatore."}</DialogDescription>
          </DialogHeader>

          {error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" data-testid="user-form-error">{error}</div>
          )}

          <div className="space-y-3.5">
            <div className="space-y-1.5">
              <Label>Nome completo</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="user-name-input" placeholder="Mario Rossi" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email} disabled={!!editing}
                onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" placeholder="nome@esseci-elight.it" />
            </div>
            <div className="space-y-1.5">
              <Label>{editing ? "Nuova password (opzionale)" : "Password"}</Label>
              <Input type="password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" placeholder="Almeno 8 caratteri" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Ruolo</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="operatore">Operatore sportello</SelectItem>
                    <SelectItem value="backoffice">Back Office (tutte le sedi)</SelectItem>
                    <SelectItem value="admin">Direzione / Titolare</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Sede</Label>
                <Select value={form.office_id || "none"} onValueChange={(v) => setForm({ ...form, office_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="user-office-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">Nessuna</SelectItem>
                    {offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Telefono (opzionale)</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="user-phone-input" placeholder="+39 ..." />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="user-cancel-button">Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="user-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
